import type { ModalFuncProps, TableColumnsType } from 'antd'
import { Avatar, Button, Flex, message, Modal, Table, Tooltip } from 'antd'
import { useAttemptListener } from 'dev-masters-react-kit'
import { useEffect, useMemo } from 'react'
import { HiMiniNoSymbol } from 'react-icons/hi2'
import { MdDelete, MdEdit } from 'react-icons/md'
import { useNavigate } from 'react-router-dom'
import {
  attemptDeletePlaylist,
  attemptGetPlaylistsList,
  Playlist,
  PlaylistOrderBy,
} from '../utils/redux/playlistsSlice'
import { listenerMiddleware, store, useAppDispatch, useAppSelector } from '../utils/redux/store'
import usePagination from '../utils/hooks/usePagination'
import { getSongDuration } from '../utils/tools'

const basicColumns: TableColumnsType<Playlist> = [
  {
    title: 'Poster',
    dataIndex: 'image',
    render: (value) => (
      <Avatar
        shape="square"
        src={`${process.env.REACT_APP_UPLOADED_BASE_URL}/playlists/${value}`}
        // src={`${process.env.REACT_APP_PLAYLISTS_BASE_URL}/${value}`}
        icon={<HiMiniNoSymbol />}
      />
    ),
    width: 80,
  },
  {
    title: 'Title',
    dataIndex: 'title',
  },
  {
    title: 'Songs',
    dataIndex: 'songs',
    render: (value) => value?.length || 0,
  },
  {
    title: 'Duration',
    render: (_, playlist) => {
      return getSongDuration(playlist.totalDuration)
    },
    width: 120,
  },
]

export default function PlaylistsTable({
  playlists,
  total,
}: {
  playlists?: Playlist[]
  total?: number
}) {
  const dispatch = useAppDispatch()

  useAttemptListener({
    listenerMiddleware: listenerMiddleware,
    
    attempt: attemptDeletePlaylist,
    
    onFulfilled: () => {
      message.success('Playlist has been removed successfully!')
    },
  })
  const columns: TableColumnsType<Playlist> = useMemo(() => {
    return [
      ...basicColumns,
      {
        title: 'Action',
        key: 'action',
        width: 120,
        render: (_, playlist) => (
          <TableActions
            confirm={{
              title: `Are you sure you want to delete ${playlist.title} playlist?`,
            }}
            onDelete={() => {
              dispatch(attemptDeletePlaylist(playlist._id))
            }}
            onEdit={() => {
              navigate(`/music/playlists/playlist-edit/${playlist._id}`)
            }}
          />
        ),
      },
    ]
  }, [])

  const navigate = useNavigate()
  const isLoading = useAttemptListener({
    listenerMiddleware: listenerMiddleware,

    attempt: attemptGetPlaylistsList,
  })
  const { pagination, onPaginationChange } = usePagination({
    fetchCallback: fetchPlaylists,
  })

  function fetchPlaylists(page: number, pageSize: number) {
    const skip = (page - 1) * pageSize
    dispatch(
      attemptGetPlaylistsList({
        skip,
        limit: pageSize,
        oderBy: PlaylistOrderBy.TITLE,
      }),
    )
  }

  useEffect(() => {
    fetchPlaylists(pagination.current, pagination.pageSize)
  }, [])

  return (
    <>
      <Table<Playlist>
        size={'small'}
        sticky
        columns={columns}
        rowKey={(playlist) => playlist._id}
        // onRow={(playlist, rowIndex) => {
        //   return {
        //     onClick: (event) => {
        //       navigate(`/music/playlist-edit/${playlist._id}`)
        //       // editPlaylistDrawerRef.current?.open(playlist)
        //     },
        //   }
        // }}
        dataSource={playlists}
        pagination={{
          ...pagination,
          total,
          onChange: onPaginationChange,
        }}
        loading={isLoading}

        // scroll={{ y: '70vh' }}
      />
    </>
  )
}

export interface PlaylistActionsInterface {
  confirm?: ModalFuncProps
  onEdit: () => void
  onDelete?: () => void
}

export function TableActions({
  onEdit,
  onDelete,
  ..._confirm
}: PlaylistActionsInterface) {
  const { confirm } = _confirm
  return (
    <Flex gap={10}>
      <Tooltip title="Edit">
        <Button onClick={onEdit} icon={<MdEdit />} size="small" />
      </Tooltip>
      <Tooltip title="Delete">
        {onDelete && (
          <Button
            onClick={() => {
              Modal.confirm({
                width: 'auto',
                onOk: onDelete,
                ...confirm,
              })
            }}
            icon={<MdDelete />}
            size="small"
          />
        )}
      </Tooltip>
    </Flex>
  )
}
